import { useState, useEffect } from 'react';
import moment from 'moment';

/**
 * Custom hook for managing date range state
 * @param {Object} options - Configuration options
 * @param {boolean} options.useCurrentYear - Whether to default to current year or half year
 * @param {boolean} options.useStartOfWeek - Whether to default to start of this week through today
 * @returns {Object} Date range state and handlers
 */
const useDateRange = (options = {}) => {
  const { useCurrentYear = false, useStartOfWeek = false } = options;

  const [handleDate, setHandleDate] = useState({
    startDate: '',
    endDate: '',
  });
  const [inputs, setInputs] = useState({
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    const today = moment().format('YYYY-MM-DD');
    let defaultStartDate;

    if (useStartOfWeek) {
      defaultStartDate = moment().startOf('week');
    } else if (!useCurrentYear && moment().get('month') >= 7) {
      defaultStartDate = moment().startOf('year').add(6, 'months');
    } else {
      defaultStartDate = moment().startOf('year');
    }

    const startDateFormatted = defaultStartDate.format('YYYY-MM-DD');

    setInputs({
      startDate: defaultStartDate.startOf('day')._d.getTime(),
      endDate: moment().endOf('day')._d.getTime(),
    });

    setHandleDate({
      startDate: startDateFormatted,
      endDate: today,
    });
  }, [useCurrentYear, useStartOfWeek]);

  const handleDateChange = (field, value) => {
    const newHandleDate = { ...handleDate, [field]: value };
    setHandleDate(newHandleDate);

    setInputs({
      ...inputs,
      startDate: newHandleDate.startDate
        ? moment(newHandleDate.startDate).startOf('day')._d.getTime()
        : null,
      endDate: newHandleDate.endDate
        ? moment(newHandleDate.endDate).endOf('day')._d.getTime()
        : null,
    });
  };

  const handleStartDateChange = (value) => {
    handleDateChange('startDate', value);
  };

  const handleEndDateChange = (value) => {
    handleDateChange('endDate', value);
  };

  return {
    // Display values (YYYY-MM-DD format for inputs)
    startDate: handleDate.startDate,
    endDate: handleDate.endDate,

    // Timestamp values for API calls
    startDateTimestamp: inputs.startDate,
    endDateTimestamp: inputs.endDate,

    // Change handlers
    handleStartDateChange,
    handleEndDateChange,
    handleDateChange,

    // Raw state (for advanced usage)
    handleDate,
    inputs,
  };
};

export default useDateRange;
